import type { KeyboardEvent } from 'react';

/**
 * Keyboard event handler that triggers a click event on the target element
 * when Enter or Space key is pressed. This is useful for making non-button elements
 * keyboard accessible while reusing the existing onClick handler.
 *
 * @param e - The keyboard event
 * @param options - Optional configuration
 * @param options.disabled - If true, the click event will not be dispatched
 * @param options.stopPropagation - If true, stops event propagation
 *
 * @example
 * // Simple usage - reuses the onClick handler
 * <div
 *   role="button"
 *   tabIndex={0}
 *   onClick={handleClick}
 *   onKeyDown={handleKeyboardClick}
 * >
 *   Click me
 * </div>
 *
 * @example
 * // With options for disabled state or stopPropagation
 * <div
 *   role="button"
 *   tabIndex={disabled ? -1 : 0}
 *   onClick={handleClick}
 *   onKeyDown={(e) => handleKeyboardClick(e, { disabled, stopPropagation: true })}
 * >
 *   Click me
 * </div>
 */
export const handleKeyboardClick = <T extends Element>(
  e: KeyboardEvent<T>,
  options?: { disabled?: boolean; stopPropagation?: boolean },
): void => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    if (options?.stopPropagation) {
      e.stopPropagation();
    }
    if (!options?.disabled) {
      e.currentTarget.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
        }),
      );
    }
  }
};

/* Creates a keyboard event handler that triggers a click action on Enter or Space key.
 * This is essential for making non-button elements (like divs with role="button")
 * accessible to keyboard users.
 *
 * @param onClick - The function to call when Enter or Space is pressed
 * @param options - Optional configuration
 * @param options.disabled - If true, the handler will not trigger the onClick
 * @param options.stopPropagation - If true, stops event propagation (default: false)
 *
 * @example
 * <div
 *   role="button"
 *   tabIndex={0}
 *   onClick={handleClick}
 *   onKeyDown={createKeyboardClickHandler(handleClick)}
 * >
 *   Click me
 * </div>
 */
export const createKeyboardClickHandler =
  (
    onClick: () => void,
    options?: { disabled?: boolean; stopPropagation?: boolean },
  ) =>
  (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (options?.stopPropagation) {
        e.stopPropagation();
      }
      if (!options?.disabled) {
        onClick();
      }
    }
  };
